import type { ForecastVersion } from "../types"

export interface VersionTreeNode {
  version: ForecastVersion
  children: VersionTreeNode[]
}

export interface RestoreMetaRow {
  label: string
  value: string
}

/** Groups versions under their `parent_version_id` when the parent exists in the list. */
export function build_version_forest(versions: ForecastVersion[]): VersionTreeNode[] {
  const by_id = new Map(versions.map((v: ForecastVersion) => [v.id, v]))
  const child_lists = new Map<string, ForecastVersion[]>()
  for (const v of versions) {
    const pid = v.parent_version_id
    if (pid && by_id.has(pid)) {
      const list = child_lists.get(pid) ?? []
      list.push(v)
      child_lists.set(pid, list)
    }
  }
  for (const list of child_lists.values()) {
    list.sort(
      (a: ForecastVersion, b: ForecastVersion) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  }
  const roots = versions.filter(
    (v: ForecastVersion) => !v.parent_version_id || !by_id.has(v.parent_version_id)
  )
  roots.sort((a: ForecastVersion, b: ForecastVersion) => {
    if (a.is_primary !== b.is_primary) {
      return a.is_primary ? -1 : 1
    }
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })
  function build(v: ForecastVersion): VersionTreeNode {
    const children = (child_lists.get(v.id) ?? []).map(build)
    return { version: v, children }
  }
  return roots.map(build)
}

/**
 * Parses server-written restore descriptions (label/value blocks separated by blank lines).
 * Legacy UUID-heavy descriptions return a single friendly row.
 */
export function parse_restore_description_to_table(description: string | undefined | null): RestoreMetaRow[] | null {
  if (!description?.trim()) {
    return null
  }
  const d = description.trim()
  const is_new_format =
    d.includes("This plan was created by undoing") ||
    (d.includes("Source scenario\n") && d.includes("When (that save)\n"))

  if (!is_new_format) {
    if (/changelog event/i.test(d) && /[0-9a-f]{8}-[0-9a-f]{4}/i.test(d)) {
      return [
        {
          label: "Details",
          value:
            "This version was saved before we formatted restore details. Open Changelog on the source scenario to see drivers, notes, and exact changes.",
        },
      ]
    }
    return null
  }

  const rows: RestoreMetaRow[] = []
  const blocks = d.split(/\n\n+/)
  for (const block of blocks) {
    const lines = block.split("\n").map((line: string) => line.trimEnd())
    if (lines.length < 2) {
      continue
    }
    const label = lines[0].trim()
    if (label.length === 0 || label.startsWith("This plan was created")) {
      continue
    }
    const value = lines.slice(1).join("\n").trim()
    if (!value) {
      continue
    }
    rows.push({ label, value })
  }
  return rows.length > 0 ? rows : null
}

/** Truncate very long auto-generated scenario titles in dense lists. */
export function list_title_for_version_card(name: string): string {
  if (name.length <= 88) {
    return name
  }
  return `${name.slice(0, 85)}…`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parse_restore_description_to_table", () => {
    it("parses label blocks from server restore description", () => {
      const desc = [
        "This plan was created by undoing a prior save on the source scenario.",
        "",
        "Source scenario",
        "Main Plan",
        "",
        "When (that save)",
        "May 08, 2026 · 06:23 PM UTC",
        "",
        "Driver",
        "Promo",
        "",
        "Forecast change (plan dollars)",
        "+$10,000.00",
      ].join("\n")
      const rows = parse_restore_description_to_table(desc)
      expect(rows?.find((r) => r.label === "Driver")?.value).toBe("Promo")
      expect(rows?.find((r) => r.label === "Source scenario")?.value).toBe("Main Plan")
    })

    it("returns legacy hint when UUID changelog text present", () => {
      const rows = parse_restore_description_to_table(
        "Forecast state before changelog event 019e08d4-fc1b-b66e-71b0-b49807778879 (foo)"
      )
      expect(rows?.[0].label).toBe("Details")
      expect(rows?.[0].value).toContain("formatted restore details")
    })
  })

  describe("build_version_forest", () => {
    it("nests children under parent", () => {
      const versions: ForecastVersion[] = [
        {
          id: "p",
          name: "Parent",
          is_primary: true,
          created_at: "2026-01-01",
          updated_at: "2026-01-02",
          data: [],
          periods: [],
          compare_mode: false,
          comparison_periods: [],
        },
        {
          id: "c",
          name: "Child",
          parent_version_id: "p",
          is_primary: false,
          created_at: "2026-01-01",
          updated_at: "2026-01-03",
          data: [],
          periods: [],
          compare_mode: false,
          comparison_periods: [],
        },
      ]
      const forest = build_version_forest(versions)
      expect(forest).toHaveLength(1)
      expect(forest[0].version.id).toBe("p")
      expect(forest[0].children).toHaveLength(1)
      expect(forest[0].children[0].version.id).toBe("c")
    })
  })
}
